import { signInAnonymously } from 'firebase/auth';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import styles from 'AppRoutes/BeerStorePayment/styles';
import PixPaymentPanel from 'components/PixPaymentPanel';
import { useGrowlerCart } from 'services/cart/GrowlerCartContext';
import { auth } from 'services/firebase/client';
import { createOrderDocument } from 'services/paymentService';
import {
  GROWLER_CUSTOMER_STORAGE_KEY,
  GROWLER_ORDER_STORAGE_KEY,
} from 'utils/types/GrowlerCustomer';

import {
  buildGrowlerOrderItems,
  buildGrowlerOrderPayload,
  getGrowlerItemsForPayment,
  type GrowlerOrderData,
} from './growlerOrderUtils';

const GROWLER_PIX_STORAGE_KEY = 'growlerPixPaymentId';

interface GrowlerPixTabProps {
  total: number;
  subtotal: number;
  shippingCost: number;
  discount: number;
  cashbackRedeemed?: number;
  orderData: GrowlerOrderData | null;
  disabled?: boolean;
}

const GrowlerPixTab = ({
  total,
  subtotal,
  shippingCost,
  discount,
  cashbackRedeemed = 0,
  orderData,
  disabled = false,
}: GrowlerPixTabProps) => {
  const navigate = useNavigate();
  const { cart, clearCart } = useGrowlerCart();

  const customerEmail = orderData?.customer?.email || '';

  const contextKey = useMemo(
    () =>
      `growlers-${orderData?.customer?.id || 'order'}-${total}-${cart.items.length}-${cashbackRedeemed}`,
    [cart.items.length, cashbackRedeemed, orderData?.customer?.id, total],
  );

  const resolveOrderData = useCallback((): GrowlerOrderData | null => {
    if (orderData?.customer && orderData.shipping) {
      return orderData;
    }

    const savedOrderData = localStorage.getItem(GROWLER_ORDER_STORAGE_KEY);
    if (!savedOrderData) {
      return null;
    }

    try {
      return JSON.parse(savedOrderData) as GrowlerOrderData;
    } catch {
      return null;
    }
  }, [orderData]);

  const ensureAuth = useCallback(async () => {
    if (!auth.currentUser) {
      await signInAnonymously(auth);
    }
  }, []);

  const handlePaymentApproved = useCallback(
    async (paymentId: string | number) => {
      await ensureAuth();

      const currentOrderData = resolveOrderData();
      if (!currentOrderData?.customer || !currentOrderData.shipping) {
        throw new Error('Dados do pedido não encontrados');
      }

      if (!auth.currentUser?.uid) {
        throw new Error('Usuário não autenticado');
      }

      const payerEmail = currentOrderData.customer.email;
      const orderItems = buildGrowlerOrderItems(cart.items);
      const orderPayload = buildGrowlerOrderPayload({
        userId: auth.currentUser.uid,
        userEmail: payerEmail,
        items: orderItems,
        subtotal,
        shippingCost,
        discount,
        cashbackRedeemed,
        total,
        orderData: currentOrderData,
        payment: {
          method: 'pix',
          status: 'approved',
          transactionId: paymentId,
          paymentId,
          amount: total,
        },
      });

      const cfOrder = await createOrderDocument(orderPayload);

      clearCart();
      localStorage.removeItem(GROWLER_ORDER_STORAGE_KEY);
      localStorage.removeItem(GROWLER_CUSTOMER_STORAGE_KEY);

      navigate('/growlers/success', {
        state: {
          orderId: cfOrder.id,
          orderNumber: cfOrder.orderNumber,
          total,
          items: cart.items,
          paymentId,
          cashbackCredited: cfOrder.cashbackCredited || 0,
          cashbackRedeemed: cfOrder.cashbackRedeemed || cashbackRedeemed,
          userEmail: cfOrder.userEmail || payerEmail,
          needsPasswordSetup: Boolean(cfOrder.needsPasswordSetup),
        },
      });
    },
    [
      cart.items,
      cashbackRedeemed,
      clearCart,
      discount,
      ensureAuth,
      navigate,
      resolveOrderData,
      shippingCost,
      subtotal,
      total,
    ],
  );

  return (
    <PixPaymentPanel
      key={contextKey}
      total={total}
      cpf={orderData?.customer?.cpf || ''}
      email={customerEmail}
      description="Clube da 42 - Growlers"
      cartItems={getGrowlerItemsForPayment(cart.items)}
      isSubscriber={false}
      storageKey={GROWLER_PIX_STORAGE_KEY}
      contextKey={contextKey}
      disabled={disabled}
      styles={styles}
      onPaymentApproved={handlePaymentApproved}
      onBeforeCreatePix={ensureAuth}
    />
  );
};

export default GrowlerPixTab;
